
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiCalendar,
    FiCheckCircle,
    FiClock,
    FiCopy,
    FiCreditCard,
    FiEdit3,
    FiEye,
    FiFileText,
    FiFilter,
    FiLoader,
    FiPlus,
    FiPrinter,
    FiRefreshCw,
    FiRotateCcw,
    FiSearch,
    FiTrash2,
    FiXCircle,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import {
    cancelSaleInvoice,
    confirmSaleInvoice,
    deleteSaleInvoice,
    duplicateSaleInvoice,
    fetchSaleInvoiceSummary,
    fetchSaleInvoices,
    saleInvoiceFiltersSelector,
    saleInvoiceListSelector,
    saleInvoiceLoadingSelector,
    saleInvoicePaginationSelector,
    saleInvoiceSummarySelector,
    setSaleInvoiceFilters,
    setSaleInvoicePage,
    setSaleInvoiceLimit,
} from "../../../../../../store/billing";

import SaleInvoicePaymentDialog from "./SaleInvoicePaymentDialog";
import InvoiceActions, {
    ACTION_KEYS,
} from "./components/InvoiceActions";

import "./SaleInvoiceManagement.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const LIST_ROUTE =
    "/billing/sale-invoices";

const DEFAULT_FILTERS = {
    search: "",
    documentStatus: "",
    paymentStatus: "",
    salesChannel: "",
    startDate: "",
    endDate: "",
    overdueOnly: false,
};

const DEFAULT_PAGINATION = {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
};

const DOCUMENT_STATUS_OPTIONS = [
    {
        value: "",
        label: "All Invoice Statuses",
    },
    {
        value: "DRAFT",
        label: "Draft",
    },
    {
        value: "CONFIRMED",
        label: "Confirmed",
    },
    {
        value: "CANCELLED",
        label: "Cancelled",
    },
];

const PAYMENT_STATUS_OPTIONS = [
    {
        value: "",
        label: "All Payment Statuses",
    },
    {
        value: "UNPAID",
        label: "Unpaid",
    },
    {
        value: "PARTIALLY_PAID",
        label: "Partially Paid",
    },
    {
        value: "PAID",
        label: "Paid",
    },
    {
        value: "REFUNDED",
        label: "Refunded",
    },
];

const SALES_CHANNEL_OPTIONS = [
    {
        value: "",
        label: "All Sales Channels",
    },
    {
        value: "STORE",
        label: "Store",
    },
    {
        value: "ONLINE",
        label: "Online",
    },
    {
        value: "PHONE",
        label: "Phone",
    },
    {
        value: "WHATSAPP",
        label: "WhatsApp",
    },
    {
        value: "MARKETPLACE",
        label: "Marketplace",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const parsed =
        Number(value);

    return Number.isFinite(
        parsed
    )
        ? parsed
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
        100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    ).format(date);
};

const formatLabel = (
    value
) => {
    if (!value) {
        return "-";
    }

    return String(value)
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );
};

const getInvoiceId = (
    invoice
) =>
    invoice?._id ||
    invoice?.id ||
    "";

const getInvoiceNumber = (
    invoice
) =>
    invoice?.documentNumber ||
    invoice?.invoiceNumber ||
    invoice?.invoiceNo ||
    "-";

const getCustomerName = (
    invoice
) =>
    invoice?.customerSnapshot
        ?.customerName ||
    invoice?.customerSnapshot
        ?.name ||
    invoice?.customer
        ?.customerName ||
    invoice?.customer?.name ||
    invoice?.customerName ||
    "Walk-in Customer";

const getCustomerMobile = (
    invoice
) =>
    invoice?.customerSnapshot
        ?.mobile ||
    invoice?.customer
        ?.mobile ||
    invoice?.customerMobile ||
    "";

const getGrandTotal = (
    invoice
) =>
    toNumber(
        invoice?.totals
            ?.grandTotal ??
        invoice?.grandTotal ??
        invoice?.totalAmount
    );

const getPaidAmount = (
    invoice
) =>
    toNumber(
        invoice?.totals
            ?.paidAmount ??
        invoice?.paymentSummary
            ?.paidAmount ??
        invoice?.paidAmount
    );

const getBalanceAmount = (
    invoice
) => {
    const savedBalance =
        invoice?.totals
            ?.balanceAmount ??
        invoice?.paymentSummary
            ?.balanceAmount ??
        invoice?.balanceAmount;

    if (
        savedBalance !==
        undefined &&
        savedBalance !==
        null
    ) {
        return Math.max(
            roundMoney(
                savedBalance
            ),
            0
        );
    }

    return Math.max(
        roundMoney(
            getGrandTotal(
                invoice
            ) -
            getPaidAmount(
                invoice
            )
        ),
        0
    );
};

const normalizeDocumentStatus = (
    invoice
) =>
    String(
        invoice?.documentStatus ||
        invoice?.status ||
        "DRAFT"
    ).toUpperCase();

const normalizePaymentStatus = (
    invoice
) =>
    String(
        invoice?.paymentStatus ||
        invoice?.paymentSummary
            ?.paymentStatus ||
        "UNPAID"
    ).toUpperCase();

const canOpenInvoicePayment = (
    invoice
) => {
    const documentStatus =
        normalizeDocumentStatus(
            invoice
        );

    const paymentStatus =
        normalizePaymentStatus(
            invoice
        );

    return (
        [
            "DRAFT",
            "CONFIRMED",
        ].includes(
            documentStatus
        ) &&
        [
            "UNPAID",
            "PARTIALLY_PAID",
        ].includes(
            paymentStatus
        ) &&
        getBalanceAmount(
            invoice
        ) >
        0
    );
};

const canRecordInvoicePayment = (
    invoice
) => {
    const documentStatus =
        normalizeDocumentStatus(
            invoice
        );

    const paymentStatus =
        normalizePaymentStatus(
            invoice
        );

    return (
        documentStatus ===
        "CONFIRMED" &&
        [
            "UNPAID",
            "PARTIALLY_PAID",
        ].includes(
            paymentStatus
        ) &&
        getBalanceAmount(
            invoice
        ) >
        0
    );
};

const getResultDocument = (
    result
) => {
    const payload =
        result?.payload ||
        result ||
        {};

    return (
        payload?.invoice ||
        payload?.saleInvoice ||
        payload?.document ||
        payload?.data?.invoice ||
        payload?.data
            ?.saleInvoice ||
        payload?.data
            ?.document ||
        (
            payload?._id
                ? payload
                : null
        )
    );
};

const getErrorMessage = (
    error,
    fallback
) =>
    error?.message ||
    error?.payload ||
    error?.response?.data
        ?.message ||
    fallback;

const getListRecords = (
    listState
) => {
    if (
        Array.isArray(
            listState
        )
    ) {
        return listState;
    }

    if (
        Array.isArray(
            listState
                ?.records
        )
    ) {
        return listState
            .records;
    }

    if (
        Array.isArray(
            listState
                ?.invoices
        )
    ) {
        return listState
            .invoices;
    }

    if (
        Array.isArray(
            listState
                ?.data
        )
    ) {
        return listState
            .data;
    }

    return [];
};

const getSummaryValue = (
    summary,
    key,
    type
) => {
    const section =
        summary?.[key] ||
        {};

    if (
        type ===
        "amount"
    ) {
        return toNumber(
            section.amount ??
            section.totalAmount ??
            summary?.[
            `${key}Amount`
            ] ??
            0
        );
    }

    return toNumber(
        section.count ??
        section.totalCount ??
        summary?.[
        `${key}Count`
        ] ??
        0
    );
};

/*
|--------------------------------------------------------------------------
| SUMMARY CARD
|--------------------------------------------------------------------------
*/

const SummaryCard = ({
    title,
    amount,
    count,
    icon,
    className,
    active,
    onClick,
}) => (
    <button
        type="button"
        className={[
            "sale-invoice-summary-card",
            className,
            active
                ? "sale-invoice-summary-card-active"
                : "",
        ]
            .filter(Boolean)
            .join(" ")}
        onClick={
            onClick
        }
    >
        <span className="sale-invoice-summary-card-icon">
            {icon}
        </span>

        <span className="sale-invoice-summary-card-content">
            <span className="sale-invoice-summary-card-label">
                {title}
            </span>

            <strong>
                {formatCurrency(
                    amount
                )}
            </strong>

            <small>
                {count} invoice
                {count === 1
                    ? ""
                    : "s"}
            </small>
        </span>
    </button>
);

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SaleInvoiceManagement = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const listState =
        useSelector(
            saleInvoiceListSelector
        );

    const summary =
        useSelector(
            saleInvoiceSummarySelector
        ) || {};

    const reduxFilters =
        useSelector(
            saleInvoiceFiltersSelector
        ) || {};

    const reduxPagination =
        useSelector(
            saleInvoicePaginationSelector
        ) || {};

    const loadingState =
        useSelector(
            saleInvoiceLoadingSelector
        ) || {};

    const [
        filters,
        setFilters,
    ] = useState({
        ...DEFAULT_FILTERS,
        ...reduxFilters,
    });

    const [
        appliedFilters,
        setAppliedFilters,
    ] = useState({
        ...DEFAULT_FILTERS,
        ...reduxFilters,
    });

    const [
        moreFiltersOpen,
        setMoreFiltersOpen,
    ] = useState(false);

    const [
        selectedPaymentInvoice,
        setSelectedPaymentInvoice,
    ] = useState(null);

    const [
        paymentDialogOpen,
        setPaymentDialogOpen,
    ] = useState(false);

    const [
        loadingAction,
        setLoadingAction,
    ] = useState("");

    const [
        pageError,
        setPageError,
    ] = useState("");

    const pagination = {
        ...DEFAULT_PAGINATION,
        ...reduxPagination,
    };

    const invoices =
        useMemo(
            () =>
                getListRecords(
                    listState
                ),
            [listState]
        );

    const listLoading =
        Boolean(
            loadingState.listLoading ||
            loadingState
                .fetchLoading ||
            loadingState.loading
        );

    const summaryLoading =
        Boolean(
            loadingState
                .summaryLoading
        );

    const fetchData =
        useCallback(
            async (
                nextFilters =
                    appliedFilters,
                nextPagination =
                    pagination
            ) => {
                setPageError("");

                const params = {
                    ...nextFilters,
                    page:
                        nextPagination
                            .page ||
                        1,
                    limit:
                        nextPagination
                            .limit ||
                        10,
                };

                Object.keys(
                    params
                ).forEach(
                    (
                        key
                    ) => {
                        if (
                            params[
                            key
                            ] ===
                            "" ||
                            params[
                            key
                            ] ===
                            false ||
                            params[
                            key
                            ] ===
                            null ||
                            params[
                            key
                            ] ===
                            undefined
                        ) {
                            delete params[
                                key
                            ];
                        }
                    }
                );

                try {
                    await Promise.all([
                        dispatch(
                            fetchSaleInvoices(
                                params
                            )
                        ).unwrap(),

                        dispatch(
                            fetchSaleInvoiceSummary()
                        ).unwrap(),
                    ]);
                } catch (
                error
                ) {
                    setPageError(
                        getErrorMessage(
                            error,
                            "Unable to load sale invoices."
                        )
                    );
                }
            },
            [
                appliedFilters,
                dispatch,
                pagination,
            ]
        );

    useEffect(() => {
        fetchData();
        // Initial load and whenever page/limit changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        pagination.page,
        pagination.limit,
    ]);

    const updateFilter =
        useCallback(
            (
                field,
                value
            ) => {
                setFilters(
                    (
                        current
                    ) => ({
                        ...current,
                        [field]:
                            value,
                    })
                );
            },
            []
        );

    const applyFilters =
        useCallback(() => {
            const nextFilters = {
                ...filters,
            };

            setAppliedFilters(
                nextFilters
            );

            dispatch(
                setSaleInvoiceFilters(
                    nextFilters
                )
            );

            dispatch(
                setSaleInvoicePage(
                    1
                )
            );

            fetchData(
                nextFilters,
                {
                    ...pagination,
                    page: 1,
                }
            );
        }, [
            dispatch,
            fetchData,
            filters,
            pagination,
        ]);

    const resetFilters =
        useCallback(() => {
            const reset = {
                ...DEFAULT_FILTERS,
            };

            setFilters(
                reset
            );

            setAppliedFilters(
                reset
            );

            dispatch(
                setSaleInvoiceFilters(
                    reset
                )
            );

            dispatch(
                setSaleInvoicePage(
                    1
                )
            );

            fetchData(
                reset,
                {
                    ...pagination,
                    page: 1,
                }
            );
        }, [
            dispatch,
            fetchData,
            pagination,
        ]);

    const applySummaryFilter =
        useCallback(
            (
                type
            ) => {
                let nextFilters = {
                    ...DEFAULT_FILTERS,
                };

                if (
                    type ===
                    "UNPAID"
                ) {
                    nextFilters = {
                        ...nextFilters,
                        documentStatus:
                            "CONFIRMED",
                        paymentStatus:
                            "UNPAID",
                    };
                }

                if (
                    type ===
                    "PARTIALLY_PAID"
                ) {
                    nextFilters = {
                        ...nextFilters,
                        documentStatus:
                            "CONFIRMED",
                        paymentStatus:
                            "PARTIALLY_PAID",
                    };
                }

                if (
                    type ===
                    "OVERDUE"
                ) {
                    nextFilters = {
                        ...nextFilters,
                        documentStatus:
                            "CONFIRMED",
                        overdueOnly:
                            true,
                    };
                }

                if (
                    type ===
                    "PAID"
                ) {
                    nextFilters = {
                        ...nextFilters,
                        documentStatus:
                            "CONFIRMED",
                        paymentStatus:
                            "PAID",
                    };
                }

                if (
                    type ===
                    "TODAY"
                ) {
                    const today =
                        new Date()
                            .toISOString()
                            .slice(
                                0,
                                10
                            );

                    nextFilters = {
                        ...nextFilters,
                        documentStatus:
                            "CONFIRMED",
                        startDate:
                            today,
                        endDate:
                            today,
                    };
                }

                setFilters(
                    nextFilters
                );

                setAppliedFilters(
                    nextFilters
                );

                dispatch(
                    setSaleInvoiceFilters(
                        nextFilters
                    )
                );

                dispatch(
                    setSaleInvoicePage(
                        1
                    )
                );

                fetchData(
                    nextFilters,
                    {
                        ...pagination,
                        page: 1,
                    }
                );
            },
            [
                dispatch,
                fetchData,
                pagination,
            ]
        );

    const handleOpenPaymentDialog =
        useCallback(
            async (
                invoice
            ) => {
                const documentStatus =
                    normalizeDocumentStatus(
                        invoice
                    );

                if (
                    documentStatus ===
                    "CANCELLED"
                ) {
                    setPageError(
                        "Payment cannot be recorded for a cancelled invoice."
                    );

                    return;
                }

                if (
                    !canOpenInvoicePayment(
                        invoice
                    )
                ) {
                    setPageError(
                        "This invoice does not have an outstanding payable balance."
                    );

                    return;
                }

                let payableInvoice =
                    invoice;

                if (
                    documentStatus ===
                    "DRAFT"
                ) {
                    const shouldConfirm =
                        window.confirm(
                            `Invoice ${getInvoiceNumber(
                                invoice
                            )} is still a draft. Confirm it now and record payment?`
                        );

                    if (
                        !shouldConfirm
                    ) {
                        return;
                    }

                    const invoiceId =
                        getInvoiceId(
                            invoice
                        );

                    try {
                        setLoadingAction(
                            `CONFIRM_PAYMENT:${invoiceId}`
                        );

                        setPageError(
                            ""
                        );

                        const result =
                            await dispatch(
                                confirmSaleInvoice({
                                    documentId:
                                        invoiceId,

                                    payload:
                                        {},
                                })
                            ).unwrap();

                        payableInvoice =
                            getResultDocument(
                                result
                            ) || {
                                ...invoice,
                                documentStatus:
                                    "CONFIRMED",
                            };

                        await fetchData(
                            appliedFilters,
                            pagination
                        );
                    } catch (
                    error
                    ) {
                        setPageError(
                            getErrorMessage(
                                error,
                                "Unable to confirm this invoice before payment."
                            )
                        );

                        return;
                    } finally {
                        setLoadingAction(
                            ""
                        );
                    }
                }

                setSelectedPaymentInvoice(
                    payableInvoice
                );

                setPaymentDialogOpen(
                    true
                );
            },
            [
                appliedFilters,
                dispatch,
                fetchData,
                pagination,
            ]
        );

    const handleClosePaymentDialog =
        useCallback(() => {
            setPaymentDialogOpen(
                false
            );

            setSelectedPaymentInvoice(
                null
            );
        }, []);

    const handlePaymentSuccess =
        useCallback(
            async () => {
                handleClosePaymentDialog();

                await fetchData(
                    appliedFilters,
                    pagination
                );
            },
            [
                appliedFilters,
                fetchData,
                handleClosePaymentDialog,
                pagination,
            ]
        );

    const handleDuplicate =
        useCallback(
            async (
                invoice
            ) => {
                const invoiceId =
                    getInvoiceId(
                        invoice
                    );

                try {
                    setLoadingAction(
                        `${ACTION_KEYS.DUPLICATE}:${invoiceId}`
                    );

                    const result =
                        await dispatch(
                            duplicateSaleInvoice({
                                documentId:
                                    invoiceId,
                                payload: {},
                            })
                        ).unwrap();

                    const duplicated =
                        getResultDocument(
                            result
                        );

                    if (
                        duplicated?._id
                    ) {
                        history.push(
                            `${LIST_ROUTE}/${duplicated._id}/edit`
                        );

                        return;
                    }

                    await fetchData();
                } catch (
                error
                ) {
                    setPageError(
                        getErrorMessage(
                            error,
                            "Unable to duplicate invoice."
                        )
                    );
                } finally {
                    setLoadingAction(
                        ""
                    );
                }
            },
            [
                dispatch,
                fetchData,
                history,
            ]
        );

    const handleCancel =
        useCallback(
            async (
                invoice
            ) => {
                const invoiceId =
                    getInvoiceId(
                        invoice
                    );

                const reason =
                    window.prompt(
                        "Enter cancellation reason:"
                    );

                if (
                    !reason ||
                    !reason.trim()
                ) {
                    return;
                }

                try {
                    setLoadingAction(
                        `${ACTION_KEYS.CANCEL}:${invoiceId}`
                    );

                    await dispatch(
                        cancelSaleInvoice({
                            documentId:
                                invoiceId,
                            payload: {
                                reason:
                                    reason.trim(),
                                cancellationReason:
                                    reason.trim(),
                            },
                        })
                    ).unwrap();

                    await fetchData();
                } catch (
                error
                ) {
                    setPageError(
                        getErrorMessage(
                            error,
                            "Unable to cancel invoice."
                        )
                    );
                } finally {
                    setLoadingAction(
                        ""
                    );
                }
            },
            [
                dispatch,
                fetchData,
            ]
        );

    const handleDelete =
        useCallback(
            async (
                invoice
            ) => {
                const invoiceId =
                    getInvoiceId(
                        invoice
                    );

                if (
                    !window.confirm(
                        `Delete draft invoice ${getInvoiceNumber(
                            invoice
                        )}?`
                    )
                ) {
                    return;
                }

                try {
                    setLoadingAction(
                        `${ACTION_KEYS.DELETE}:${invoiceId}`
                    );

                    await dispatch(
                        deleteSaleInvoice(
                            invoiceId
                        )
                    ).unwrap();

                    await fetchData();
                } catch (
                error
                ) {
                    setPageError(
                        getErrorMessage(
                            error,
                            "Unable to delete draft invoice."
                        )
                    );
                } finally {
                    setLoadingAction(
                        ""
                    );
                }
            },
            [
                dispatch,
                fetchData,
            ]
        );

    const handlePageChange =
        useCallback(
            (
                nextPage
            ) => {
                if (
                    nextPage <
                    1 ||
                    nextPage >
                    pagination
                        .totalPages
                ) {
                    return;
                }

                dispatch(
                    setSaleInvoicePage(
                        nextPage
                    )
                );
            },
            [
                dispatch,
                pagination,
            ]
        );

    const handleLimitChange =
        useCallback(
            (
                event
            ) => {
                const nextLimit =
                    Number(
                        event
                            .target
                            .value
                    );

                dispatch(
                    setSaleInvoiceLimit(
                        nextLimit
                    )
                );

                dispatch(
                    setSaleInvoicePage(
                        1
                    )
                );
            },
            [
                dispatch,
                pagination,
            ]
        );

    const todayAmount =
        getSummaryValue(
            summary,
            "todaySales",
            "amount"
        );

    const todayCount =
        getSummaryValue(
            summary,
            "todaySales",
            "count"
        );

    const unpaidAmount =
        getSummaryValue(
            summary,
            "unpaid",
            "amount"
        );

    const unpaidCount =
        getSummaryValue(
            summary,
            "unpaid",
            "count"
        );

    const partialAmount =
        getSummaryValue(
            summary,
            "partiallyPaid",
            "amount"
        );

    const partialCount =
        getSummaryValue(
            summary,
            "partiallyPaid",
            "count"
        );

    const overdueAmount =
        getSummaryValue(
            summary,
            "overdue",
            "amount"
        );

    const overdueCount =
        getSummaryValue(
            summary,
            "overdue",
            "count"
        );

    const paidAmount =
        getSummaryValue(
            summary,
            "paid",
            "amount"
        );

    const paidCount =
        getSummaryValue(
            summary,
            "paid",
            "count"
        );

    return (
            <div className="sale-invoice-page">
                <header className="sale-invoice-page-header">
                    <div className="sale-invoice-page-heading">
                        <span className="sale-invoice-page-heading-icon">
                            <FiFileText />
                        </span>

                        <div>
                            <h1>
                                Sale Invoices
                            </h1>

                            <p>
                                Create, collect payment, print, duplicate, cancel and track customer invoices.
                            </p>
                        </div>
                    </div>

                    <div className="sale-invoice-header-actions">
                        <button
                            type="button"
                            className="sale-invoice-refresh-button"
                            disabled={
                                listLoading ||
                                summaryLoading
                            }
                            onClick={() =>
                                fetchData()
                            }
                        >
                            {listLoading ||
                                summaryLoading ? (
                                <FiLoader className="sale-invoice-spin" />
                            ) : (
                                <FiRefreshCw />
                            )}

                            Refresh
                        </button>

                        <button
                            type="button"
                            className="sale-invoice-create-button"
                            onClick={() =>
                                history.push(
                                    `${LIST_ROUTE}/create`
                                )
                            }
                        >
                            <FiPlus />

                            Create Invoice
                        </button>
                    </div>
                </header>

                {pageError && (
                    <div className="sale-invoice-page-error">
                        <FiXCircle />

                        <span>
                            {pageError}
                        </span>
                    </div>
                )}

                <section className="sale-invoice-summary-grid">
                    <SummaryCard
                        title="Today's Sales"
                        amount={
                            todayAmount
                        }
                        count={
                            todayCount
                        }
                        icon={
                            <FiCreditCard />
                        }
                        className="sale-invoice-summary-blue"
                        active={
                            Boolean(
                                appliedFilters
                                    .startDate &&
                                appliedFilters
                                    .endDate
                            )
                        }
                        onClick={() =>
                            applySummaryFilter(
                                "TODAY"
                            )
                        }
                    />

                    <SummaryCard
                        title="Unpaid"
                        amount={
                            unpaidAmount
                        }
                        count={
                            unpaidCount
                        }
                        icon={
                            <FiXCircle />
                        }
                        className="sale-invoice-summary-red"
                        active={
                            appliedFilters
                                .paymentStatus ===
                            "UNPAID"
                        }
                        onClick={() =>
                            applySummaryFilter(
                                "UNPAID"
                            )
                        }
                    />

                    <SummaryCard
                        title="Partially Paid"
                        amount={
                            partialAmount
                        }
                        count={
                            partialCount
                        }
                        icon={
                            <FiClock />
                        }
                        className="sale-invoice-summary-orange"
                        active={
                            appliedFilters
                                .paymentStatus ===
                            "PARTIALLY_PAID"
                        }
                        onClick={() =>
                            applySummaryFilter(
                                "PARTIALLY_PAID"
                            )
                        }
                    />

                    <SummaryCard
                        title="Overdue"
                        amount={
                            overdueAmount
                        }
                        count={
                            overdueCount
                        }
                        icon={
                            <FiCalendar />
                        }
                        className="sale-invoice-summary-purple"
                        active={
                            appliedFilters
                                .overdueOnly ===
                            true
                        }
                        onClick={() =>
                            applySummaryFilter(
                                "OVERDUE"
                            )
                        }
                    />

                    <SummaryCard
                        title="Paid"
                        amount={
                            paidAmount
                        }
                        count={
                            paidCount
                        }
                        icon={
                            <FiCheckCircle />
                        }
                        className="sale-invoice-summary-green"
                        active={
                            appliedFilters
                                .paymentStatus ===
                            "PAID"
                        }
                        onClick={() =>
                            applySummaryFilter(
                                "PAID"
                            )
                        }
                    />
                </section>

                <section className="sale-invoice-filter-card">
                    <div className="sale-invoice-filter-main-row">
                        <div className="sale-invoice-search-box">
                            <FiSearch />

                            <input
                                type="text"
                                value={
                                    filters.search
                                }
                                placeholder="Search invoice number, customer, mobile or reference"
                                onChange={(
                                    event
                                ) =>
                                    updateFilter(
                                        "search",
                                        event.target
                                            .value
                                    )
                                }
                                onKeyDown={(
                                    event
                                ) => {
                                    if (
                                        event.key ===
                                        "Enter"
                                    ) {
                                        applyFilters();
                                    }
                                }}
                            />
                        </div>

                        <button
                            type="button"
                            className="sale-invoice-filter-apply"
                            onClick={
                                applyFilters
                            }
                        >
                            Apply
                        </button>

                        <select
                            value={
                                filters
                                    .documentStatus
                            }
                            onChange={(
                                event
                            ) =>
                                updateFilter(
                                    "documentStatus",
                                    event.target
                                        .value
                                )
                            }
                        >
                            {DOCUMENT_STATUS_OPTIONS.map(
                                (
                                    option
                                ) => (
                                    <option
                                        key={
                                            option.value
                                        }
                                        value={
                                            option.value
                                        }
                                    >
                                        {option.label}
                                    </option>
                                )
                            )}
                        </select>

                        <select
                            value={
                                filters
                                    .paymentStatus
                            }
                            onChange={(
                                event
                            ) =>
                                updateFilter(
                                    "paymentStatus",
                                    event.target
                                        .value
                                )
                            }
                        >
                            {PAYMENT_STATUS_OPTIONS.map(
                                (
                                    option
                                ) => (
                                    <option
                                        key={
                                            option.value
                                        }
                                        value={
                                            option.value
                                        }
                                    >
                                        {option.label}
                                    </option>
                                )
                            )}
                        </select>

                        <button
                            type="button"
                            className="sale-invoice-filter-toggle"
                            onClick={() =>
                                setMoreFiltersOpen(
                                    (
                                        current
                                    ) =>
                                        !current
                                )
                            }
                        >
                            <FiFilter />

                            More Filters
                        </button>

                    </div>

                    {moreFiltersOpen && (
                        <div className="sale-invoice-filter-advanced-row">
                            <select
                                value={
                                    filters
                                        .salesChannel
                                }
                                onChange={(
                                    event
                                ) =>
                                    updateFilter(
                                        "salesChannel",
                                        event.target
                                            .value
                                    )
                                }
                            >
                                {SALES_CHANNEL_OPTIONS.map(
                                    (
                                        option
                                    ) => (
                                        <option
                                            key={
                                                option.value
                                            }
                                            value={
                                                option.value
                                            }
                                        >
                                            {option.label}
                                        </option>
                                    )
                                )}
                            </select>

                            <input
                                type="date"
                                value={
                                    filters
                                        .startDate
                                }
                                onChange={(
                                    event
                                ) =>
                                    updateFilter(
                                        "startDate",
                                        event.target
                                            .value
                                    )
                                }
                            />

                            <input
                                type="date"
                                value={
                                    filters
                                        .endDate
                                }
                                onChange={(
                                    event
                                ) =>
                                    updateFilter(
                                        "endDate",
                                        event.target
                                            .value
                                    )
                                }
                            />

                            <label className="sale-invoice-overdue-toggle">
                                <input
                                    type="checkbox"
                                    checked={
                                        Boolean(
                                            filters
                                                .overdueOnly
                                        )
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        updateFilter(
                                            "overdueOnly",
                                            event.target
                                                .checked
                                        )
                                    }
                                />

                                Overdue only
                            </label>
                        </div>
                    )}

                    <button
                        type="button"
                        className="sale-invoice-filter-reset"
                        onClick={
                            resetFilters
                        }
                    >
                        <FiRotateCcw />

                        Reset
                    </button>
                </section>

                <section className="sale-invoice-table-card">
                    <div className="sale-invoice-table-header">
                        <div>
                            <h2>
                                Invoice Register
                            </h2>

                            <p>
                                {pagination.total ||
                                    invoices.length}{" "}
                                records found
                            </p>
                        </div>
                    </div>

                    {listLoading ? (
                        <div className="sale-invoice-table-loading">
                            <FiLoader className="sale-invoice-spin" />

                            Loading sale invoices...
                        </div>
                    ) : (
                        <div className="sale-invoice-table-scroll">
                            <table className="sale-invoice-table">
                                <thead>
                                    <tr>
                                        <th>
                                            Invoice
                                        </th>

                                        <th>
                                            Date / Due
                                        </th>

                                        <th>
                                            Customer
                                        </th>

                                        <th>
                                            Channel
                                        </th>

                                        <th className="sale-invoice-text-right">
                                            Total
                                        </th>

                                        <th className="sale-invoice-text-right">
                                            Paid
                                        </th>

                                        <th className="sale-invoice-text-right">
                                            Balance
                                        </th>

                                        <th>
                                            Status
                                        </th>

                                        <th>
                                            Payment
                                        </th>

                                        <th>
                                            Fulfilment
                                        </th>

                                        <th>
                                            Return
                                        </th>

                                        <th className="sale-invoice-actions-column">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {invoices.map(
                                        (
                                            invoice
                                        ) => {
                                            const invoiceId =
                                                getInvoiceId(
                                                    invoice
                                                );

                                            const documentStatus =
                                                normalizeDocumentStatus(
                                                    invoice
                                                );

                                            const paymentStatus =
                                                normalizePaymentStatus(
                                                    invoice
                                                );

                                            const canPay =
                                                canOpenInvoicePayment(
                                                    invoice
                                                );

                                            const actionBusy =
                                                loadingAction.endsWith(
                                                    `:${invoiceId}`
                                                );

                                            return (
                                                <tr
                                                    key={
                                                        invoiceId
                                                    }
                                                    className={
                                                        documentStatus ===
                                                            "CANCELLED"
                                                            ? "sale-invoice-row-cancelled"
                                                            : ""
                                                    }
                                                >
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="sale-invoice-number-button"
                                                            onClick={() =>
                                                                history.push(
                                                                    `${LIST_ROUTE}/${invoiceId}/view`
                                                                )
                                                            }
                                                        >
                                                            {getInvoiceNumber(
                                                                invoice
                                                            )}
                                                        </button>
                                                    </td>

                                                    <td>
                                                        <div className="sale-invoice-date-cell">
                                                            <strong>
                                                                {formatDate(
                                                                    invoice
                                                                        ?.documentDate
                                                                )}
                                                            </strong>

                                                            <small>
                                                                Due{" "}
                                                                {formatDate(
                                                                    invoice
                                                                        ?.dueDate
                                                                )}
                                                            </small>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="sale-invoice-customer-cell">
                                                            <strong>
                                                                {getCustomerName(
                                                                    invoice
                                                                )}
                                                            </strong>

                                                            <small>
                                                                {getCustomerMobile(
                                                                    invoice
                                                                )}
                                                            </small>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <span className="sale-invoice-channel">
                                                            {formatLabel(
                                                                invoice
                                                                    ?.salesChannel ||
                                                                "STORE"
                                                            )}
                                                        </span>
                                                    </td>

                                                    <td className="sale-invoice-text-right sale-invoice-amount-strong">
                                                        {formatCurrency(
                                                            getGrandTotal(
                                                                invoice
                                                            )
                                                        )}
                                                    </td>

                                                    <td className="sale-invoice-text-right">
                                                        {formatCurrency(
                                                            getPaidAmount(
                                                                invoice
                                                            )
                                                        )}
                                                    </td>

                                                    <td className="sale-invoice-text-right sale-invoice-balance-amount">
                                                        {formatCurrency(
                                                            getBalanceAmount(
                                                                invoice
                                                            )
                                                        )}
                                                    </td>

                                                    <td>
                                                        <span
                                                            className={`sale-invoice-badge sale-invoice-status-${documentStatus.toLowerCase()}`}
                                                        >
                                                            {formatLabel(
                                                                documentStatus
                                                            )}
                                                        </span>
                                                    </td>

                                                    <td>
                                                        {canPay ? (
                                                            <button
                                                                type="button"
                                                                className={`sale-invoice-payment-clickable sale-invoice-payment-${paymentStatus.toLowerCase()}`}
                                                                title={
                                                                    documentStatus ===
                                                                        "DRAFT"
                                                                        ? "Confirm invoice and record payment"
                                                                        : "Click to record payment"
                                                                }
                                                                onClick={() =>
                                                                    handleOpenPaymentDialog(
                                                                        invoice
                                                                    )
                                                                }
                                                            >
                                                                <FiCreditCard />

                                                                {formatLabel(
                                                                    paymentStatus
                                                                )}
                                                            </button>
                                                        ) : (
                                                            <span
                                                                className={`sale-invoice-badge sale-invoice-payment-${paymentStatus.toLowerCase()}`}
                                                                title={
                                                                    documentStatus ===
                                                                        "CANCELLED"
                                                                        ? "Payment cannot be recorded for a cancelled invoice"
                                                                        : "No outstanding payable balance"
                                                                }
                                                            >
                                                                {formatLabel(
                                                                    paymentStatus
                                                                )}
                                                            </span>
                                                        )}
                                                    </td>

                                                    <td>
                                                        <span
                                                            className={`sale-invoice-badge sale-invoice-fulfillment-${String(
                                                                invoice
                                                                    ?.fulfillmentStatus ||
                                                                "NOT_APPLICABLE"
                                                            ).toLowerCase()}`}
                                                        >
                                                            {formatLabel(
                                                                invoice
                                                                    ?.fulfillmentStatus ||
                                                                "NOT_APPLICABLE"
                                                            )}
                                                        </span>
                                                    </td>

                                                    <td>
                                                        <span
                                                            className={`sale-invoice-badge sale-invoice-return-${String(
                                                                invoice
                                                                    ?.returnStatus ||
                                                                "NOT_RETURNED"
                                                            ).toLowerCase()}`}
                                                        >
                                                            {formatLabel(
                                                                invoice
                                                                    ?.returnStatus ||
                                                                "NOT_RETURNED"
                                                            )}
                                                        </span>
                                                    </td>

                                                    <td>
                                                        <div className="sale-invoice-row-actions">
                                                            <button
                                                                type="button"
                                                                title="View invoice"
                                                                onClick={() =>
                                                                    history.push(
                                                                        `${LIST_ROUTE}/${invoiceId}/view`
                                                                    )
                                                                }
                                                            >
                                                                <FiEye />
                                                            </button>

                                                            {documentStatus ===
                                                                "DRAFT" && (
                                                                    <button
                                                                        type="button"
                                                                        className="sale-invoice-action-edit"
                                                                        title="Edit draft"
                                                                        onClick={() =>
                                                                            history.push(
                                                                                `${LIST_ROUTE}/${invoiceId}/edit`
                                                                            )
                                                                        }
                                                                    >
                                                                        <FiEdit3 />
                                                                    </button>
                                                                )}

                                                            {canPay && (
                                                                <button
                                                                    type="button"
                                                                    className="sale-invoice-action-payment"
                                                                    title="Record payment"
                                                                    onClick={() =>
                                                                        handleOpenPaymentDialog(
                                                                            invoice
                                                                        )
                                                                    }
                                                                >
                                                                    <FiCreditCard />
                                                                </button>
                                                            )}

                                                            <button
                                                                type="button"
                                                                title="Print invoice"
                                                                onClick={() =>
                                                                    history.push(
                                                                        `${LIST_ROUTE}/${invoiceId}/print`
                                                                    )
                                                                }
                                                            >
                                                                <FiPrinter />
                                                            </button>

                                                            <button
                                                                type="button"
                                                                title="Duplicate invoice"
                                                                disabled={
                                                                    actionBusy
                                                                }
                                                                onClick={() =>
                                                                    handleDuplicate(
                                                                        invoice
                                                                    )
                                                                }
                                                            >
                                                                {loadingAction ===
                                                                    `${ACTION_KEYS.DUPLICATE}:${invoiceId}` ? (
                                                                    <FiLoader className="sale-invoice-spin" />
                                                                ) : (
                                                                    <FiCopy />
                                                                )}
                                                            </button>

                                                            {documentStatus ===
                                                                "CONFIRMED" && (
                                                                    <button
                                                                        type="button"
                                                                        className="sale-invoice-action-warning"
                                                                        title="Cancel invoice"
                                                                        disabled={
                                                                            actionBusy
                                                                        }
                                                                        onClick={() =>
                                                                            handleCancel(
                                                                                invoice
                                                                            )
                                                                        }
                                                                    >
                                                                        <FiXCircle />
                                                                    </button>
                                                                )}

                                                            {documentStatus ===
                                                                "DRAFT" && (
                                                                    <button
                                                                        type="button"
                                                                        className="sale-invoice-action-danger"
                                                                        title="Delete draft"
                                                                        disabled={
                                                                            actionBusy
                                                                        }
                                                                        onClick={() =>
                                                                            handleDelete(
                                                                                invoice
                                                                            )
                                                                        }
                                                                    >
                                                                        <FiTrash2 />
                                                                    </button>
                                                                )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    )}

                                    {!invoices.length && (
                                        <tr>
                                            <td
                                                colSpan="12"
                                                className="sale-invoice-table-empty"
                                            >
                                                No sale invoices found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="sale-invoice-pagination">
                        <span className="sale-invoice-pagination-info">
                            Page{" "}
                            {pagination.page} of{" "}
                            {pagination.totalPages} —{" "}
                            {pagination.total ||
                                invoices.length}{" "}
                            records
                        </span>

                        <div className="sale-invoice-pagination-controls">
                            <label>
                                Rows
                            </label>

                            <select
                                value={
                                    pagination.limit
                                }
                                onChange={
                                    handleLimitChange
                                }
                            >
                                <option value="10">
                                    10
                                </option>

                                <option value="20">
                                    20
                                </option>

                                <option value="50">
                                    50
                                </option>

                                <option value="100">
                                    100
                                </option>
                            </select>

                            <button
                                type="button"
                                disabled={
                                    pagination.page <=
                                    1
                                }
                                onClick={() =>
                                    handlePageChange(
                                        pagination.page -
                                        1
                                    )
                                }
                            >
                                Previous
                            </button>

                            <button
                                type="button"
                                aria-current="page"
                            >
                                {pagination.page}
                            </button>

                            <button
                                type="button"
                                disabled={
                                    pagination.page >=
                                    pagination
                                        .totalPages
                                }
                                onClick={() =>
                                    handlePageChange(
                                        pagination.page +
                                        1
                                    )
                                }
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </section>

                {paymentDialogOpen &&
                    selectedPaymentInvoice && (
                        <SaleInvoicePaymentDialog
                            open={
                                paymentDialogOpen
                            }
                            invoice={
                                selectedPaymentInvoice
                            }
                            onClose={
                                handleClosePaymentDialog
                            }
                            onSuccess={
                                handlePaymentSuccess
                            }
                        />
                    )}
            </div>
    );
};

export {
    DEFAULT_FILTERS,
    formatCurrency,
    formatDate,
    formatLabel,
    getInvoiceId,
    getInvoiceNumber,
    getCustomerName,
    getGrandTotal,
    getPaidAmount,
    getBalanceAmount,
    canOpenInvoicePayment,
    canRecordInvoicePayment,
};

export default SaleInvoiceManagement;